import React, { useState } from "react";
import PropTypes from "prop-types";
import { scorecardHeaders, scorecardDataFields, orderedGroupingKeys, backgroundColors, dataFieldsGroups, expansionHeaders } from "./ReportDetailedViewConstants";
import '../Reports.css';

export default function ExpandableTable({ data, expandedRows, setExpandedRows }) {
    const handleExpandClick = (id) => {
        let level = expandedRows.includes(id)
            ? id.split("--").length
            : id.split("--").length + Math.max(...expandedRows.filter(rowId => rowId.includes(id)).map(rowId => rowId.split("--").length), 1)
        setExpansionHeader(expansionHeadersList.slice(0, level))
        setExpandedRows((prev) =>
            prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
        );
    };

    let groupingKeys = orderedGroupingKeys;
    let expansionHeadersList = expansionHeaders;

    if (data.every(d => !d?.BranchSourceOfBusiness || d.BranchSourceOfBusiness === "N/A")) {
        groupingKeys = groupingKeys.filter(key => key !== "BranchSourceOfBusinessAndName");
        expansionHeadersList = expansionHeadersList.filter(header => header !== "Branch");
    }

    const [expansionHeader, setExpansionHeader] = useState([expansionHeadersList[0]]);

    function groupDataByKey(data, keyToGroupBy) {
        return data.reduce((acc, item) => {
            const groupingValue = keyToGroupBy === "ReportingDate"
                ? new Date(item[keyToGroupBy]).toLocaleDateString()
                : item[keyToGroupBy];

            if (!acc[groupingValue]) {
                acc[groupingValue] = { sum: {}, data: [], count: {} };
            }

            scorecardDataFields.filter(field => !field.includes("conversion")).forEach(key => {
                acc[groupingValue].sum[key] = (acc[groupingValue].sum[key] || 0) + item[key];
            });

            dataFieldsGroups.forEach(group => {
                const quotes = acc[groupingValue]?.sum[group + "_quotes"];
                if (quotes > 0) {
                    let conversion = acc[groupingValue].sum[group + "_new_business"] / quotes;
                    acc[groupingValue].sum[group + "_conversion"] = conversion;
                }
            });

            acc[groupingValue].data.push(item);
            return acc;
        }, {});
    }

    const formatNumberForDisplay = (field, value) => {
        if (value === undefined || Number.isNaN(value) || value === 0) return '';

        const fieldType = field.split("_").pop();
        const prefix = fieldType === 'premium' ? '$' : '';
        const suffix = fieldType === 'conversion' ? '%' : '';

        if (fieldType === 'conversion') { value = value * 100; }
        const fractionDigits = fieldType === 'premium' || fieldType === 'conversion' ? 2 : 0;
        const number = value.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits });

        return `${prefix}${number}${suffix}`;
    };

    function Row({ rowId, header, summedData, expansionData, level, currentMaxLevel }) {
        const indentation = { paddingLeft: `${level * 15}px` };
        const backgroundColor = (level === currentMaxLevel && level > 1) ? { backgroundColor: backgroundColors[level] } : {};

        function formatHeader(headerToFormat) {
            if (headerToFormat?.startsWith("N/A")) {
                return "Unassigned"
            } else if (headerToFormat?.includes("N/A")) {
                return headerToFormat.split("-")[0]
            }
            return headerToFormat
        }

        return (
            <>
                <tr>
                    <td style={{ ...indentation }}>
                        {expansionData ? (
                            <>
                                <gds-icon
                                    icon-type={expandedRows.includes(rowId) ? "collapse" : "expand"}
                                    onClick={() => handleExpandClick(rowId)}
                                    style={{ cursor: "pointer" }}
                                    size="xxsmall"
                                />
                                <b>{formatHeader(header)}</b>
                            </>
                        ) : (
                            <div style={{ paddingLeft: "20px" }}><b>{formatHeader(header)}</b></div>
                        )}
                    </td>
                    {scorecardDataFields.map(field => (
                        <td className={`level${level}`} key={field}>
                            {formatNumberForDisplay(field, summedData[field])}
                        </td>
                    ))}
                </tr>
                {expandedRows.includes(rowId) &&
                    expansionData &&
                    Object.entries(groupDataByKey(expansionData, groupingKeys[level])).map(([groupingKey, { sum, data }]) => (
                        <Row
                            key={groupingKey}
                            rowId={rowId + "--" + groupingKey}
                            header={groupingKey}
                            summedData={sum}
                            handleExpandClick={handleExpandClick}
                            level={level + 1}
                            currentMaxLevel={currentMaxLevel + 1}
                            expansionData={level === groupingKeys.length - 1 ? null : data}
                        />
                    ))
                }
            </>
        );
    };

    return (
        <div className="data-table expandable-table printable">
            <table className="table">
                <thead>
                    <tr>
                        <th>{expansionHeader.join(" - ")}</th>
                        {scorecardHeaders.map(header => (
                            <th key={header}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {Object.entries(groupDataByKey(data, groupingKeys[0])).map(([groupingKey, { sum, data }]) => (
                        <Row
                            key={groupingKey}
                            rowId={"row-" + groupingKey}
                            header={groupingKey}
                            summedData={sum}
                            expansionData={data}
                            level={1}
                            currentMaxLevel={1}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

ExpandableTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object).isRequired,
    expandedRows: PropTypes.arrayOf(PropTypes.string).isRequired,
    setExpandedRows: PropTypes.func.isRequired,
};